"use client";
import { ResourceLabel } from "@cmp-src/labels/ResourceLabel/ResourceLabel";
import { Button } from "@cmp-src/primitives/button";
import type { IResource } from "@gettactic/api";
import { cn } from "@gettactic/helpers/src/css/cn";
import { X } from "lucide-react";
import React from "react";

export interface RoomSelectionRowProps {
  room: IResource;
  /** The room whose details the reserve card is currently showing. */
  isViewing: boolean;
  onView: (room: IResource) => void;
  onRemove: (roomId: string) => void;
}

const classes = {
  Row: "flex w-full items-start gap-2 rounded-lg p-1 text-left transition-colors hover:bg-neutral-50",
  RowViewing: "bg-neutral-50",
  Status: "font-semibold text-[11px] uppercase tracking-wide",
  StatusViewing: "text-muted-foreground",
  StatusAdded: "text-primary",
  RemoveButton: "size-6 shrink-0 rounded-full p-0",
  RemoveIcon: "size-3.5"
};

export const RoomSelectionRow: React.FC<RoomSelectionRowProps> = ({
  room,
  isViewing,
  onView,
  onRemove
}) => {
  const handleRemove = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onRemove(room.id);
    },
    [onRemove, room.id]
  );

  return (
    <div className={cn(classes.Row, isViewing && classes.RowViewing)}>
      <ResourceLabel
        // `min-w-0`: the hierarchy line is `nowrap`, so the office/floor text
        // gives the label a min-content width of ~315px. Without it the label
        // refuses to shrink in the narrow reserve card and pushes the status
        // and the remove button out of the row, scrolling the card sideways.
        className="min-w-0 flex-1 cursor-pointer"
        resource={room}
        imageSize="sm"
        allowFavorite={false}
        showAmenities
        maxTags={1}
        onClick={() => onView(room)}
        headingClassName="text-[15px]"
      />
      <span
        className={cn(
          classes.Status,
          isViewing ? classes.StatusViewing : classes.StatusAdded
        )}
      >
        {isViewing ? "Viewing" : "Added"}
      </span>
      <Button
        variant="icon"
        size="icon"
        className={classes.RemoveButton}
        onClick={handleRemove}
        aria-label={`Remove ${room.name}`}
      >
        <X className={classes.RemoveIcon} />
      </Button>
    </div>
  );
};
